import Entity from './bss-entity';
import logger from '@main/externals/bss-package-logger/js/logger';
import helper from '@main/externals/bss-package-helper/js/helper';
var _version;
var _toolbox;
/**
 * @class EntityBuilder
 * @desc
 */
var EntityBuilder = /** @class */ (function () {
    /**
     * @desc EntityBuilder constructor
     * @param {EventManager} eventManager - event manager object
     * @param {Formatter} formatter - formatter object
     * @param {Resolution} resolution - resolution object
     * @param {Object} toolbox - toolbox object
     * @param {Object} [components=null] - components object if is null entity loads components from UI desc
     * @param {string} [elementType='nc'] - type of the element(entity): nc, us, sc, bc, oa, pc
     */
    function EntityBuilder(eventManager, formatter, resolution, toolbox, components, elementType, standalone) {
        if (components === void 0) { components = null; }
        if (elementType === void 0) { elementType = 'nc'; }
        if (standalone === void 0) { standalone = null; }
        if (!standalone)
            helper.validateMethodParams(arguments, {
                eventManger: 'EventManager',
                formatter: 'Formatter',
                resolution: 'Resolution',
            });
        logger.info('EntityBuilder => function : constructor | Params: ', eventManager, formatter, resolution, logger, toolbox, components, elementType);
        _toolbox = toolbox;
        /**
         * Type of the element(entity):
         * nc, us, sc, bc, oa, pc
         */
        this._elementType = elementType;
        this._standalone = standalone;
        /**
         * @type {Boolean}
         * @private
         */
        this._isWidgetInWidget = null;
        /**
         * @type {String}
         * @private
         */
        this._loaderSelector = null;
        /**
         * @type {String}
         * @private
         */
        this._theme = null;
        /**
         * @type {Boolean}
         * @private
         */
        this._isLocaleAphelia = null;
        /**
         * @type {Formatter}
         * @private
         */
        this._formatter = formatter;
        /**
         * @type {Resolution}
         * @private
         */
        this._resolution = resolution;
        /**
         *
         * @type {EventManager}
         * @private
         */
        this._eventManager = eventManager;
        /**
         * @desc stores all loaded components modules
         * @type {Map}
         * @private
         */
        this._loadedComponentsMap = new Map();
        /**
         * @desc map of all created entities, key is entityId
         * @type {Map<any, any>|Map}
         * @private
         */
        this._entityMap = new Map();
        /**
         * @desc stores entity builder version
         * @type {String}
         * @private
         */
        _version = '@name ver. @version';
        /**
         * @desc stores components version string
         * @type {String}
         * @private
         */
        this._componentsVersion = '';
        /**
         * @desc set current protocol
         * @type {String}
         * @private
         */
        this._protocol = window.location.protocol + '//';
        /**
         * @desc flag tells if component are loaded async based on url from UI config
         * @type {Boolean}
         * @private
         */
        this._isAsyncComponents = true;
        if (components !== null) {
            this._isAsyncComponents = false;
            this._setComponentsMap(components);
        }
        /**
         * @desc flag tells if critical error occurs during entity initialization
         * @type {Boolean}
         * @private
         */
        this._isCriticalError = false;
        /**
         * @desc critical error message
         * @type {String}
         * @private
         */
        this._isCriticalErrorMsg = '';
        this._setBrowserInformation();
    }
    Object.defineProperty(EntityBuilder.prototype, "entities", {
        /**
         * @desc getter for entity map
         * @return {Map.<any, any>|Map}
         */
        get: function () {
            return this._entityMap;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * @desc method creates Entity based on components description
     * @param {String} entitySelector
     * @param {String} entityId
     * @param {Object} config
     * @param {Number} instanceId
     * @param {string} elementType - type of the element(entity): nc, us, sc, bc, oa, pc
     * @return {Promise}
     * @public
     */
    EntityBuilder.prototype.createEntity = function (entitySelector, entityId, config, instanceId, elementType, isWidgetInWidget, loaderSelector, theme, isLocaleAphelia, standalone) {
        if (instanceId === void 0) { instanceId = null; }
        if (standalone === void 0) { standalone = null; }
        logger.info('EntityBuilder => function : createEntity | Params', entitySelector, entityId, config);
        this._isWidgetInWidget = isWidgetInWidget;
        this._loaderSelector = loaderSelector;
        this._theme = theme;
        this._isLocaleAphelia = isLocaleAphelia;
        if (elementType)
            this._elementType = elementType;
        if (standalone)
            this._elementType = 'nc';
        return this._initEntity(entitySelector, entityId, config, instanceId);
    };
    /**
     * @desc method create Entity instance
     * @param {String} entitySelector
     * @param {String} entityId
     * @param {Object} config
     * @param {Number} instanceId
     * @return {Promise}
     * @private
     */
    EntityBuilder.prototype._initEntity = function (entitySelector, entityId, config, instanceId) {
        var _this = this;
        logger.info('EntityBuilder => function : _initEntity | Params', entitySelector, entityId, config);
        var promise;
        if (!this._standalone &&
            !this._validateUIDescription(config, instanceId, this._isAsyncComponents)) {
            return;
        }
        if (this._isAsyncComponents) {
            var groupedComponentsByType = _toolbox._.groupBy(config.layout.components, 'componentType');
            var promises_1 = this._loadComponents(groupedComponentsByType, config);
            promise = new Promise(function (resolve, rej) {
                Promise.all(promises_1).then(function (data) {
                    _this._createEntity(entitySelector, entityId, config, instanceId);
                    resolve({
                        entityId: entityId,
                    });
                });
            });
            return promise;
        }
        else {
            promise = new Promise(function (resolve, rej) {
                _this._createEntity(entitySelector, entityId, config, instanceId);
                resolve({
                    entityId: entityId,
                });
            });
            return promise;
        }
    };
    /**
     *
     * @param {String} entitySelector
     * @param {String} entityId
     * @param {Object} config
     * @param {Number} instanceId
     * @emits {bss-entity-created-$ENTITY-INSTANCE-ID}
     * @private
     */
    EntityBuilder.prototype._createEntity = function (entitySelector, entityId, config, instanceId) {
        // if component are loaded not async, do not validate
        // else check if on loaded occurs any error - if error, return
        if (this._isCriticalError ||
            (this._isAsyncComponents && !this._areAllComponentsLoaded(config))) {
            // do not create entity if critical error occurs
            this._entityErrorHandler(instanceId);
            return;
        }
        try {
            var entity = new Entity(entitySelector, entityId, config, this._loadedComponentsMap, this._eventManager, this._formatter, this._resolution, _toolbox, instanceId, this._elementType, this._isWidgetInWidget, this._loaderSelector, this._theme, this._isLocaleAphelia, this._standalone);
        }
        catch (error) {
            console.log(error);
        }
        this._entityMap.set(entityId, entity);
        this._setBrowser(entity);
        this._eventManager.publish('bss-entity-created-' + instanceId, 'bss-entity-builder', {
            entityId: entityId,
        });
        this._setComponentsVersion();
    };
    /**
     * @desc error handling function
     * @param {String} instanceId
     * @emits {bss-entity-critical-error-$ENTITY-INSTANCE-ID}
     * @private
     */
    EntityBuilder.prototype._entityErrorHandler = function (instanceId) {
        logger.info('EntityBuilder => function : _entityErrorHandler | Params', instanceId);
        var err = { errorMsg: this._isCriticalErrorMsg };
        this._eventManager.publish('bss-entity-critical-error-' + instanceId, 'bss-entity-builder', err);
    };
    /**
     * @desc validate UI Description: resultData, components IDs and components URLs
     * @param {Object} config - UI Description configuration
     * @param {String} instanceId
     * @param {Boolean} [validateComponentsUrl = false] - flag set to true when we want to validate component url from UI Desc
     * @return {Boolean} - return true if there is no error
     * @private
     */
    EntityBuilder.prototype._validateUIDescription = function (config, instanceId, validateComponentsUrl) {
        if (validateComponentsUrl === void 0) { validateComponentsUrl = false; }
        logger.info('EntityBuilder => function : _validateUIDescription | Params', config, instanceId, validateComponentsUrl);
        // check if dataResult is not empty
        if (this._checkUIResultData(config.resultData)) {
            // if no error, check if all IDs are unique
            if (this._checkUIComponentsID(config.layout.components) &&
                validateComponentsUrl) {
                // if no error and validateComponentsUrl (when component are loaded async based on url in UI), check all components have valid string as URL
                this._checkUIComponentsUrl(config.layout.components);
            }
        }
        // if critical error occurs on UI Description validation, show error page
        if (this._isCriticalError) {
            this._entityErrorHandler(instanceId);
            return false;
        }
        return true;
    };
    /**
     * @desc check if resultData in UI Description is defined
     * @param {Object} resultData - resultData from UI Description configuration
     * @return {Boolean} - return false if dataResult is undefined, null or empty
     * @private
     */
    EntityBuilder.prototype._checkUIResultData = function (resultData) {
        logger.info('EntityBuilder => function : _checkUIResultData | Params', resultData);
        if (typeof resultData === 'undefined' ||
            resultData === null ||
            _toolbox._.isEmpty(resultData)) {
            this._isCriticalError = true;
            this._isCriticalErrorMsg =
                'Error: There is no resultData in UI Description.';
            logger.error('EntityBuilder => function : _checkUIResultData: | Error: There is no resultData in UI Description.', resultData);
            return false;
        }
        return true;
    };
    /**
     * @desc check if all components have unique IDs
     * @param {Array} components - array of all components from UI Description
     * @return {Boolean} - return true if all components have unique ID
     * @private
     */
    EntityBuilder.prototype._checkUIComponentsID = function (components) {
        var _this = this;
        logger.info('EntityBuilder => function : _checkUIComponentsID | Params', components);
        var componentsById = _toolbox._.groupBy(components, 'componentId');
        var isNoError = true;
        // check if all components have unique ID
        _toolbox._.forEach(componentsById, function (componentsByIdGroup) {
            if (componentsByIdGroup.length > 1) {
                // if more than one component have the same ID, there is critical error - do not render entity
                _this._isCriticalError = true;
                _this._isCriticalErrorMsg = 'Error: Components do not have unique IDs.';
                logger.error('EntityBuilder => function : _checkUIComponentsID: | Error: Components do not have unique IDs.', componentsByIdGroup);
                isNoError = false;
                return false;
            }
        });
        return isNoError;
    };
    /**
     * @desc check if component URL is different from: undefined, null and empty string. Function does not valid if URL string is working
     * @param {Object} comp - component to check
     * @return {Boolean} - if return false if component URL is: undefined or null or empty String
     * @private
     */
    EntityBuilder.prototype._validateComponentUrl = function (comp) {
        logger.info('EntityBuilder => function : _validateComponentUrl | Params', comp);
        if (typeof comp.componentUrl === 'undefined' ||
            comp.componentUrl === null ||
            comp.componentUrl == '') {
            this._isCriticalError = true;
            this._isCriticalErrorMsg =
                'Error: Component ' + comp.componentType + ' URL string is not valid.';
            logger.error('EntityBuilder => function : _validateComponentUrl: | Error: Component ' +
                comp.componentType +
                ' URL string is not valid.', comp);
            return false;
        }
        return true;
    };
    /**
     * @desc Check if all components have valid URL (not undefinded, not null, not empty string) / currently not USED, component are built in entity
     * @param {Array} components - all components from UI Description (with unique IDs)
     * @return {Boolean} - return true if all components have valid string as URL
     * @private
     */
    EntityBuilder.prototype._checkUIComponentsUrl = function (components) {
        var _this = this;
        logger.info('EntityBuilder => function : _checkUIComponentsUrl | Params', components);
        var groupedComponentsByType = _toolbox._.groupBy(components, 'componentType');
        var isNoError = true;
        _toolbox._.forEach(groupedComponentsByType, function (componentTypeGroup) {
            // there is only 1 component of the same type, so check this component url
            if (componentTypeGroup.length == 1) {
                if (!_this._validateComponentUrl(componentTypeGroup[0])) {
                    isNoError = false;
                    return false;
                }
            }
            // there are few components of the same type, so group all components by componentUrl
            else {
                var groupedComponentsByUrl = _toolbox._.groupBy(componentTypeGroup, 'componentUrl');
                var groupedComponentsByUrlValues = _toolbox._.keys(groupedComponentsByUrl);
                var groupedComponentsByUrlLength = groupedComponentsByUrlValues.length;
                // components of the same type have different URLs - critical error
                if (groupedComponentsByUrlLength > 1) {
                    _this._isCriticalError = true;
                    _this._isCriticalErrorMsg =
                        'Error: Component ' +
                            componentTypeGroup[0].componentType +
                            ' has few valid URL strings.';
                    logger.error('EntityBuilder => function : _checkUIComponentsUrl: | Error: Component ' +
                        componentTypeGroup[0].componentType +
                        ' has few valid URL strings.', groupedComponentsByUrl);
                    isNoError = false;
                    return false;
                }
                // all components have the same URL, so check this URL
                else if (!_this._validateComponentUrl(groupedComponentsByUrl[groupedComponentsByUrlValues][0])) {
                    isNoError = false;
                    return false;
                }
            }
        });
        return isNoError;
    };
    /**
     * @desc load all components provided in param | currently not USED
     * @param {Object} groupedComponentsByType
     * @param {Object} config
     * @return {Promise[]}
     * @private
     */
    EntityBuilder.prototype._loadComponents = function (groupedComponentsByType, config) {
        var _this = this;
        logger.info('EntityBuilder => function : _loadComponents | Params', groupedComponentsByType, config);
        var promises = [];
        _toolbox._.forEach(groupedComponentsByType, function (componentsOfType) {
            var timestamp = new Date().getTime(); // let timestamp = ''; // only for testing
            var promise = new Promise(function (resolve, reject) {
                System.import(componentsOfType[0].componentUrl.replace('@protocol@', _this._protocol) +
                    '?_=' +
                    timestamp)
                    .then(function (module) {
                    _this._loadedComponentsMap.set(componentsOfType[0].componentType, {
                        module: module,
                        version: '',
                    });
                    logger.info('EntityBuilder  => function : _loadComponents:' +
                        componentsOfType[0].componentType +
                        ' loaded');
                    // set flag for all components of this type
                    _toolbox._.forEach(config.layout.components, function (compObj) {
                        if (compObj.componentType == componentsOfType[0].componentType) {
                            compObj.componentUrlIsValid = true;
                        }
                    });
                    resolve();
                })
                    .catch(function (err) {
                    _toolbox._.forEach(config.layout.components, function (compObj) {
                        if (compObj.componentType == componentsOfType[0].componentType) {
                            compObj.componentUrlIsValid = false;
                            logger.error('EntityBuilder => function : _loadComponents | Error: Component ' +
                                compObj.componentType +
                                ' has invalid URL, cannot load component.', compObj);
                        }
                    });
                    reject();
                });
            });
            promises.push(promise);
        });
        return promises;
    };
    /**
     * @desc set _loadedComponentsMap from static components object
     * @param {Object} components - object container components, key is the component name used in UI DESC, value is reference to component
     * @private
     */
    EntityBuilder.prototype._setComponentsMap = function (components) {
        var _this = this;
        logger.info('EntityBuilder => function : _setComponentsMap | Params', components);
        _toolbox._.each(components, function (v, k) {
            _this._loadedComponentsMap.set(k, {
                module: v,
                version: '',
            });
        });
    };
    /**
     * @desc init Entity when all asynchronous components are loaded
     * @param {String} entitySelector
     * @param {String} entityId
     * @param {Object} config
     * @param {Number} instanceId
     * @private
     */
    EntityBuilder.prototype._onComponentsLoaded = function (entitySelector, entityId, config, instanceId) {
        logger.info('EntityBuilder => function : _onComponentsLoaded | Params', entitySelector, entityId, config);
        return this._initEntity(entitySelector, entityId, config, instanceId);
    };
    /**
     * @desc check weather all asynchronous components are loaded
     * @param {Object} config
     * @return {Boolean} return true when all components are loaded
     * @private
     */
    EntityBuilder.prototype._areAllComponentsLoaded = function (config) {
        logger.info('EntityBuilder => function : _areAllComponentsLoaded | Params', config);
        var groupedComponentsByValidUrl = _toolbox._.groupBy(config.layout.components, 'componentUrlIsValid');
        var areAllLoaded = true;
        _toolbox._.forEach(groupedComponentsByValidUrl, function (compGroup, key) {
            if (key == 'false' || key == 'undefined') {
                _toolbox._.forEach(compGroup, function (comp) {
                    if (comp.significance == 'critical') {
                        areAllLoaded = false;
                        logger.error('EntityBuilder => function : _areAllComponentsLoaded | Error: Cannot load critical component ' +
                            comp.componentType +
                            ' - invalid URL.', comp);
                    }
                    else {
                        this._removeComponentFromUIDescription(config.layout.components, comp.componentId);
                        logger.error('EntityBuilder => function : _areAllComponentsLoaded | Error: Cannot load minor component ' +
                            comp.componentType +
                            ' - invalid URL. Remove component ' +
                            comp.componentId +
                            ' from UI Description.', comp);
                    }
                });
            }
        });
        if (!areAllLoaded) {
            this._isCriticalError = true;
            this._isCriticalErrorMsg =
                'Error: Some critical components have invalid URLs - cannot load these components.';
        }
        return areAllLoaded;
    };
    /**
     * @desc remove from UI Description component with given ID
     * @param components - array of components from UI Description
     * @param componentId - ID of component to be removed
     * @private
     */
    EntityBuilder.prototype._removeComponentFromUIDescription = function (components, componentId) {
        logger.info('EntityBuilder => function : _removeComponentFromUIDescription | Params', components, componentId);
        // remove (from config.layout.components) component with given ID only if is not 'critical'
        _toolbox._.remove(components, function (comp) {
            return comp.componentId == componentId && comp.significance != 'critical';
        });
    };
    /**
     * @desc destroys Entity by ID
     * @param {String} entityId
     */
    EntityBuilder.prototype.destroyEntity = function (entityId) {
        logger.info('EntityBuilder => function : destroyEntity | Params', entityId);
        if (this._entityMap.size && this._entityMap.get(entityId)) {
            this._entityMap.get(entityId).destroyEntity();
            this._entityMap.delete(entityId);
        }
    };
    /**
     * @desc destroys all Entities
     */
    EntityBuilder.prototype.destroyAllEntities = function () {
        logger.info('EntityBuilder => function : destroyAllEntities');
        this._entityMap.forEach(function (value, key, map) {
            value.destroyEntity(key);
            map.delete(key);
        });
    };
    /**
     * @desc provides platform and browser information
     * @private
     */
    EntityBuilder.prototype._setBrowserInformation = function () {
        logger.info('EntityBuilder => function : _setBrowserInformation');
        this._platfrom = navigator.platform;
        if (/Windows Phone 8/.test(navigator.userAgent)) {
            this._browser = 'browser-windows-phone-8';
        }
        else if (/Windows Phone OS 7.0/.test(navigator.userAgent)) {
            this._browser = 'browser-windows-phone-7';
        }
        else if (/Edge/.test(navigator.userAgent)) {
            this._browser = 'browser-edge';
        }
        else if (/Trident\/7.0/.test(navigator.userAgent)) {
            this._browser = 'browser-ie11';
        }
        else if (/Trident\/6.0/.test(navigator.userAgent)) {
            this._browser = 'browser-ie10';
        }
        else if (/Android/.test(navigator.userAgent) &&
            !/Chrome/.test(navigator.userAgent) &&
            !/(Trident)/.test(navigator.userAgent)) {
            this._browser = 'browser-android-default';
        }
        else if (/Linux/.test(navigator.userAgent) &&
            /Chrome/.test(navigator.userAgent)) {
            this._browser = 'browser-linux-chrome';
        }
        else if ((/Chrome/.test(navigator.userAgent) &&
            /Google Inc/.test(navigator.vendor)) ||
            /CriOS/i.test(navigator.userAgent)) {
            this._browser = 'browser-chrome';
        }
        else if (/Safari/.test(navigator.userAgent) &&
            /Apple Computer, Inc/.test(navigator.vendor)) {
            this._browser = 'browser-safari';
        }
        else if (/Firefox/.test(navigator.userAgent) &&
            !/Trident/.test(navigator.userAgent) &&
            !navigator.vendor) {
            this._browser = 'browser-firefox';
        }
    };
    /**
     * @desc set browser and platform css class on Entity container
     * @param {Entity} entity
     * @private
     */
    EntityBuilder.prototype._setBrowser = function (entity) {
        logger.info('EntityBuilder => function : _setBrowser | Params', entity);
        entity.entityContainer.addClass(this._platfrom);
        entity.entityContainer.addClass(this._browser);
        _toolbox.$('body').addClass(this._browser);
    };
    /**
     * @desc method creates/updates components version string
     * @private
     */
    EntityBuilder.prototype._setComponentsVersion = function () {
        var _this = this;
        logger.info('EntityBuilder => function : _setComponentsVersion');
        this._componentsVersion = '';
        this._loadedComponentsMap.forEach(function (val, key, map) {
            _this._componentsVersion += val.version + '\n';
        });
    };
    return EntityBuilder;
}());
export default EntityBuilder;
